// Asset references of a compiled bundle (delivery 2, contract sections 7 and 8). A bundle names an asset by id and
// carries its bucket KEYS in `bundle.assets`; the host that serves the page decides where a key is fetched from
// (the public host: `/m/_r/<key>`, the app host: its own file route). These two functions turn the keys into
// addresses on the page.
import { isObj } from './values.js';
/** @typedef {import('./typedefs.js').Bundle} Bundle */
/** @typedef {import('./typedefs.js').BundleAsset} BundleAsset */

/**
 * The address of one asset: `root` + the key of the narrowest image at least `width` wide (the widest when none
 * is), or of the document. Null when the asset has nothing to serve.
 * @param {BundleAsset | null | undefined} asset  an entry of `bundle.assets`
 * @param {string} [root]   where bucket keys are served on this host, ending in `/` (`/m/_r/`)
 * @param {number} [width]  the width asked for, in pixels
 * @returns {string | null}
 */
export function assetUrl(asset, root = '', width = 1600) {
  if (!isObj(asset)) return null;
  if (asset.kind === 'document') return typeof asset.key === 'string' && asset.key ? root + asset.key : null;
  const variants = (Array.isArray(asset.variants) ? asset.variants : []).filter((v) => isObj(v) && typeof v.key === 'string' && v.key && Number.isFinite(v.w));
  if (!variants.length) return null;
  const fit = variants.filter((v) => v.w >= width).sort((a, b) => a.w - b.w)[0] || [...variants].sort((a, b) => b.w - a.w)[0];
  return root + fit.key;
}

/**
 * A copy of the bundle whose gallery items of the `{ asset }` form carry a loadable `src` (and the asset's alt
 * text when they have none), so the card's gallery shows them like any package photo. An item whose asset the
 * bundle does not carry is left out; an item with its own `src` is kept as it is. Nothing else is changed and the
 * given bundle is not touched.
 * @param {Bundle} bundle
 * @param {string} [root]   where bucket keys are served on this host, ending in `/`
 * @param {number} [width]
 * @returns {Bundle}
 */
export function resolveAssets(bundle, root = '', width = 1600) {
  if (!isObj(bundle) || !isObj(bundle.content)) return bundle;
  const assets = isObj(bundle.assets) ? bundle.assets : {};
  const item = (g) => {
    if (!isObj(g) || typeof g.asset !== 'string' || typeof g.src === 'string') return [g];
    const asset = Object.hasOwn(assets, g.asset) ? assets[g.asset] : null, src = asset?.kind === 'image' ? assetUrl(asset, root, width) : null;
    return src ? [{ ...g, src, ...(g.alt === undefined && asset.alt !== undefined ? { alt: asset.alt } : {}) }] : [];
  };
  const group = (entries) => (isObj(entries) ? Object.fromEntries(Object.entries(entries).map(([k, e]) => [k, isObj(e) && Array.isArray(e.gallery) ? { ...e, gallery: e.gallery.flatMap(item) } : e])) : entries);
  const content = { ...bundle.content };
  for (const k of ['unitTypes', 'places']) if (k in content) content[k] = group(content[k]);
  return { ...bundle, content };
}
